/**
 * What the audit log is narrowed by, and the URL it is written in (`MCK-20260521-009`, C9).
 *
 * **One object, two readers.** The list and the CSV export narrow by the same
 * `ActivityFilters`, because the server narrows both by the same `LogFilters` — `_filters()` in
 * `backend/src/kalqa/domains/activity/router.py` builds one predicate for the list handler and
 * the export handler precisely so that *"the CSV respects the active filter"* is structurally
 * true rather than maintained. A second copy on this side would undo that from the outside.
 *
 * TWIN: `_filters()` in `domains/activity/router.py` and `LogFilters` in that domain's
 * `service.py`. A parameter added there is a field here and a line in `activityQuery()`
 * (`./api.ts`). A parameter this file forgets is a filter nobody can link to; a parameter
 * `api.ts` forgets is a filter the server never hears about.
 *
 * **The URL is the state, not a copy of it.** Nothing here is component state that also gets
 * written to the address bar: the search params are read on every render and a change is a
 * navigation. An audit log is the screen people quote to each other — *"look at what happened
 * to Blok B last week"* is a link or it is a screenshot — so `searchFromFilters` is the exact
 * inverse of `filtersFromSearch` rather than approximately so.
 *
 * **`entity_id` is deliberately not here.** The server takes it and this screen offers no
 * control for it, so carrying it in the URL would leave a filter applied that the bar cannot
 * show and *Clear* cannot undo — the failure `features/tasks/filters.ts` names for the slicer
 * and answers by keeping the two apart. When a row's own deep link needs it, it arrives with a
 * control beside it.
 *
 * **The date range is half-open, and that is the server's reading, not this file's.** `since`
 * is inclusive and `until` exclusive, so the picked *until* day is included by sending the
 * instant that starts the day after it — the same translation `tasks/filters.ts` makes for the
 * due range, in the reader's own zone for the same reason.
 */

/** Everything the list and the export narrow by. Every field present, `null` or `[]` for absent. */
export interface ActivityFilters {
  /** Any-of, over user ids. `[]` is *anybody*. */
  readonly actor: readonly string[];
  /** Any-of, over the backend's `event_type` spelling. */
  readonly eventType: readonly string[];
  /** Any-of, over the backend's `entity_type` spelling. */
  readonly entityType: readonly string[];
  /** An ISO instant. Inclusive — the server compares `event_timestamp_utc >= since`. */
  readonly since: string | null;
  /** An ISO instant. Exclusive — the server compares `event_timestamp_utc < until`. */
  readonly until: string | null;
  /**
   * Free text (`KLQ-20260821-003` §B), trimmed, `null` for none. The server matches it against
   * the event's values — codes, level names, version numbers — and never against a person, so
   * the box's own copy says what it searches.
   */
  readonly q: string | null;
}

/** The whole log: no narrowing at all. */
export const NO_FILTERS: ActivityFilters = {
  actor: [],
  eventType: [],
  entityType: [],
  since: null,
  until: null,
  q: null,
};

/**
 * The URL form: flat, string-valued, and readable by somebody typing one by hand.
 *
 * The three sets are comma-joined rather than repeated, the call the drawings slicer and the
 * tasks register already made: `?event=review_requested,review_decided` is the shape a person
 * can paste into a chat message, and the repeated form the API wants is produced once, in
 * `api.ts`.
 */
export interface ActivitySearch {
  readonly actor?: string;
  readonly event?: string;
  readonly entity?: string;
  readonly since?: string;
  readonly until?: string;
  /** The search term, trimmed. */
  readonly q?: string;
  /**
   * The project's phase context (`KLQ-20260827-002`), parsed and preserved rather than owned.
   * It belongs to the chrome above the tab row and travels between tabs, so a filter change
   * here must not silently drop it — same stance as `TaskSearch.phase`.
   */
  readonly phase?: string;
}

/** An instant. A string that is not a date is dropped rather than sent for the API to refuse. */
function instant(value: unknown): string | null {
  if (typeof value !== 'string' || value === '') return null;
  return Number.isNaN(Date.parse(value)) ? null : value;
}

/**
 * The server's cap on `q`. A longer term from a pasted URL is cut here rather than sent for a
 * `422`.
 *
 * TWIN: `_MAX_SEARCH` in `backend/src/kalqa/domains/activity/router.py`.
 */
export const MAX_SEARCH = 200;

/** A search term: trimmed, capped, and dropped when nothing is left — a blank box is no search. */
export function searchTerm(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const term = value.trim().slice(0, MAX_SEARCH).trim();
  return term === '' ? null : term;
}

function id(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * One set's members, sorted and de-duplicated on the way into the URL.
 *
 * `?event=a,b` and `?event=b,a` are one question, so they get one written form, one query key
 * and one cache entry rather than two of each (`KLQ-20260825-003` §E, applied here).
 */
function sortedSet(value: unknown): readonly string[] {
  if (typeof value !== 'string' || value === '') return [];
  return [...new Set(value.split(',').filter((part) => part !== ''))].sort();
}

/**
 * `validateSearch` for the activity route: an arbitrary query string, narrowed to what this
 * screen understands.
 *
 * A parameter that fails to validate is **dropped, not defaulted**. On an audit log that is the
 * stricter half of the rule: a filter nobody can see applied is a log that looks empty, which is
 * the one wrong answer this screen must never give (`MCK-20260521-005` invariant 6).
 */
export function parseActivitySearch(raw: Record<string, unknown>): ActivitySearch {
  const actor = sortedSet(raw.actor);
  const event = sortedSet(raw.event);
  const entity = sortedSet(raw.entity);
  const since = instant(raw.since);
  const until = instant(raw.until);
  const q = searchTerm(raw.q);
  const phase = id(raw.phase);

  return {
    ...(actor.length === 0 ? {} : { actor: actor.join(',') }),
    ...(event.length === 0 ? {} : { event: event.join(',') }),
    ...(entity.length === 0 ? {} : { entity: entity.join(',') }),
    ...(since === null ? {} : { since }),
    ...(until === null ? {} : { until }),
    ...(q === null ? {} : { q }),
    ...(phase === null ? {} : { phase }),
  };
}

/** The URL, as the object the list and the export read. */
export function filtersFromSearch(search: ActivitySearch): ActivityFilters {
  // No normalisation here: every `ActivitySearch` this reads has been through
  // `parseActivitySearch` (the route's `validateSearch`), which already sorted and de-duplicated.
  return {
    actor: sortedSet(search.actor),
    eventType: sortedSet(search.event),
    entityType: sortedSet(search.entity),
    since: search.since ?? null,
    until: search.until ?? null,
    q: search.q ?? null,
  };
}

/**
 * The object, as the URL to navigate to. The inverse of `filtersFromSearch`, and the only
 * writer — a control changes this object and navigates; it never edits a parameter.
 */
export function searchFromFilters(filters: ActivityFilters): ActivitySearch {
  return parseActivitySearch({
    actor: filters.actor.join(','),
    event: filters.eventType.join(','),
    entity: filters.entityType.join(','),
    since: filters.since,
    until: filters.until,
    q: filters.q,
  });
}

/**
 * One narrowing currently applied, per value — the collapsed bar's chip row
 * (`KLQ-20260910-001` §B). The tasks register's `narrowings`, on this screen's object: chips
 * are per value, and the trigger's count is per distinct `field`.
 */
export interface Narrowing {
  readonly field: keyof ActivityFilters;
  readonly value: string;
}

export function narrowings(filters: ActivityFilters): readonly Narrowing[] {
  const out: Narrowing[] = [];
  const each = (field: keyof ActivityFilters, values: readonly string[]): void => {
    for (const value of values) out.push({ field, value });
  };
  each('actor', filters.actor);
  each('eventType', filters.eventType);
  each('entityType', filters.entityType);
  if (filters.since !== null) out.push({ field: 'since', value: dayOfSince(filters.since) });
  if (filters.until !== null) out.push({ field: 'until', value: dayOfUntil(filters.until) });
  // The term gets a chip like any other narrowing, so *Clear* appears for it and a search that
  // emptied the page reads in the same row as the filters that could have.
  if (filters.q !== null) out.push({ field: 'q', value: filters.q });
  return out;
}

/** The filter object after one narrowing is taken off — the chip's cross, in one place. */
export function withoutNarrowing(
  filters: ActivityFilters,
  narrowing: Narrowing,
): ActivityFilters {
  const { field, value } = narrowing;
  if (field === 'since' || field === 'until' || field === 'q') {
    return { ...filters, [field]: null };
  }
  return {
    ...filters,
    [field]: filters[field].filter((one) => one !== value),
  };
}

/** Whether anything is narrowing the log — what the *Clear* button branches on. */
export function isNarrowed(filters: ActivityFilters): boolean {
  return (
    filters.actor.length > 0 ||
    filters.eventType.length > 0 ||
    filters.entityType.length > 0 ||
    filters.since !== null ||
    filters.until !== null ||
    filters.q !== null
  );
}

function pad(value: number): string {
  return value < 10 ? `0${String(value)}` : String(value);
}

function dayOf(at: Date): string {
  return `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

function midnight(day: string, plusDays: number): string | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (parts === null) return null;
  const at = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3]) + plusDays,
    0,
    0,
    0,
    0,
  );
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The `<input type="date">` value for `since` — the day its instant falls in. */
export function dayOfSince(at: string | null): string {
  if (at === null) return '';
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime()) ? '' : dayOf(parsed);
}

/** The same for `until`, which sits one millisecond into the day *after* the one shown. */
export function dayOfUntil(at: string | null): string {
  if (at === null) return '';
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime()) ? '' : dayOf(new Date(parsed.getTime() - 1));
}

/** `since` from a picked day: that day's first moment, in the reader's own zone. */
export function sinceFromDay(day: string): string | null {
  return day === '' ? null : midnight(day, 0);
}

/** `until` from a picked day: the first moment after it, so the day itself is included. */
export function untilFromDay(day: string): string | null {
  return day === '' ? null : midnight(day, 1);
}
